
import React from 'react'
import { createRoot } from 'react-dom/client'
import { ClerkProvider } from '@clerk/react'
import { shadcn } from '@clerk/ui/themes'
import App from './App.tsx'
import { captureSignupCodeFromUrl } from './lib/signupCode'
import { CLERK_PUBLISHABLE_KEY, preconnectToClerk } from './lib/clerkConfig'
import { installStaleBuildReload } from './lib/staleBuild'
import '@fontsource-variable/inter'
import '@fontsource-variable/plus-jakarta-sans'
import '@fontsource-variable/jetbrains-mono'
import './index.css'

// Clerk instance: see lib/clerkConfig.ts. clerk-js / @clerk/ui follow Clerk's latest 6.x / 1.x (the SDK default).
if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key");
}

preconnectToClerk(CLERK_PUBLISHABLE_KEY);
// Split page files from a previous deploy are gone after a release: reload once onto the new build.
installStaleBuildReload();

// Before Clerk mounts: a sign-in round-trip would drop `?code=` from the URL.
captureSignupCodeFromUrl();

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {/* Redirect FALLBACKS: where Clerk sends a user when a sign-in/sign-up completes
        without a redirect_url in hand (e.g. a retry after a failed attempt). Without
        these the production instance falls back to its home URL — the bare marketing
        domain — and the user lands on gizmodata.com instead of the app. */}
    <ClerkProvider
      appearance={{
        theme: shadcn,
        variables: {
          colorPrimary: 'hsl(var(--primary))',
          colorPrimaryForeground: 'hsl(var(--primary-foreground))',
          colorBackground: 'hsl(var(--card))',
          colorForeground: 'hsl(var(--card-foreground))',
          colorInput: 'hsl(var(--background))',
          colorInputForeground: 'hsl(var(--foreground))',
          colorMuted: 'hsl(var(--muted))',
          colorMutedForeground: 'hsl(var(--muted-foreground))',
          colorNeutral: 'hsl(var(--foreground))',
          colorDanger: 'hsl(var(--destructive))',
          colorRing: 'hsl(var(--ring) / .3)',
          colorModalBackdrop: 'rgb(2 6 23 / .55)',
          borderRadius: '.625rem',
          fontFamily: 'Inter Variable, Inter, system-ui, sans-serif',
        },
      }}
      publishableKey={CLERK_PUBLISHABLE_KEY}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      // With 3+ social providers Clerk labels block buttons with the bare provider name; say what
      // the button does (sign-in / sign-up pages show them full-width, one per row).
      localization={{ socialButtonsBlockButtonManyInView: 'Continue with {{provider|titleize}}' }}
    >
      <App />
    </ClerkProvider>
  </React.StrictMode>
);
